import axios from 'axios';
import { useCallback, useMemo, useState } from 'react';
import Form, { Validations } from '../../../../components/Form';
import { formatParam, isDate } from '../../../Control/Content/helpers';
import { ControlItem } from '../../../Control/types';

import ServicePaths from '../../../../bridge/utils/ServicePaths';

interface Props {
  botId: string;
}

const CbrtRangeForm = ({ botId }: Props) => {
  const [open, setOpen] = useState<string | undefined>();
  const [direction, setDirection] = useState<string | undefined>();
  const [date, setDate] = useState<string | undefined>();

  const [errors, setErrors] = useState<Record<string, Validations>>({});
  const [formMessage, setFormMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const checkValidation = useCallback(() => {
    /** userId can not be empty */
    let didError;

    setErrors({});

    if (typeof open !== 'undefined' && open?.length !== 0) {
      const number = parseFloat(open);

      if (isNaN(number)) {
        setErrors((prev) => ({
          ...prev,
          open: Validations.NumberTyping,
        }));

        didError = true;
      }
    }

    if (typeof date !== 'undefined' && date?.length !== 0) {
      const isValidDate = isDate(date);

      if (!isValidDate) {
        setErrors((prev) => ({
          ...prev,
          date: Validations.DateTyping,
        }));

        didError = true;
      }
    }

    if (typeof direction === 'undefined') {
      setErrors((prev) => ({
        ...prev,
        direction: Validations.Required,
      }));

      didError = true;
    } else if (direction !== 'up' && direction !== 'down') {
      setErrors((prev) => ({
        ...prev,
        direction: Validations.DirectionType,
      }));
    }

    return !didError;
  }, [date, direction, open]);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    const canContinue = checkValidation();

    if (!canContinue) {
      setIsLoading(false);
      return;
    }

    const { data } = await axios({
      method: 'POST',
      url: ServicePaths.CONTROL.SetCbrtRange,
      params: {
        botId: formatParam(botId),
        open: formatParam(open),
        direction: formatParam(direction),
        date: formatParam(date),
      },
    });

    setIsLoading(false);
    setFormMessage(data);
  }, [botId, checkValidation, date, direction, open]);

  const config = useMemo(() => {
    return {
      title: ControlItem.CbrtRange,
      formMessage,
      fields: [
        {
          label: 'Bot Id',
          value: botId,
          handleValueChange: () => {
            /** noop */
          },
          error: errors.botId,
          isLocked: true,
        },
        {
          label: 'Cbrt Range Open',
          value: open,
          handleValueChange: setOpen,
          error: errors.open,
        },
        {
          label: 'Cbrt Range Direction',
          value: direction,
          handleValueChange: setDirection,
          error: errors.direction,
        },
        {
          label: 'Cbrt Range Date',
          value: date,
          handleValueChange: setDate,
          error: errors.date,
        },
      ],
      handleSubmit,
      isLoading,
    };
  }, [
    botId,
    date,
    direction,
    errors.botId,
    errors.date,
    errors.direction,
    errors.open,
    formMessage,
    handleSubmit,
    isLoading,
    open,
  ]);

  return <Form config={config} />;
};

export default CbrtRangeForm;
